import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { MapPinned } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { canManageRoles } from '../../constants/adminRoles';
import {
  ADMIN_PROSPECTS_PAGE_SIZE,
  buildListAdminProspectActivitiesRequest,
  buildSetAdminProspectProjectionVerificationRequest,
  deleteAdminProspect,
  getAdminProspect,
  listAdminProspectActivities,
  setAdminProspectProjectionVerification,
} from '../../services/admin/prospectsService';
import { createAdminProspectRequestGate } from '../../services/admin/prospectTargetingFilters';
import { searchWithAppStepAndProperty } from '../../utils/appStepSearch';
import AdminDeleteProspectDialog from './AdminDeleteProspectDialog';
import AdminEditProspectDialog from './AdminEditProspectDialog';
import AdminEditProspectPropertyDataDialog from './AdminEditProspectPropertyDataDialog';
import AdminProspectAnalysisConversionDialog from './AdminProspectAnalysisConversionDialog';
import {
  markConversionRefreshFailed,
  markConversionWriteSuccess,
  planPostConversionRefresh,
  shouldApplyConversionResult,
} from './adminProspectAnalysisConversionController';
import AdminProspectDetailMobileSection from './AdminProspectDetailMobileSection';
import AdminProspectQuickFollowUpPanel from './AdminProspectQuickFollowUpPanel';
import ProspectImportedPropertyCard from './ProspectImportedPropertyCard';
import ProspectImportHistorySection from './ProspectImportHistorySection';
import ProspectAssociatedCampaignsSection from './ProspectAssociatedCampaignsSection';
import ProspectOwnerProvidedFinancialCard from './ProspectOwnerProvidedFinancialCard';
import ProspectOwnerProvidedPropertyCard from './ProspectOwnerProvidedPropertyCard';
import ProspectPrimaryContactCard from './ProspectPrimaryContactCard';
import ProspectPublicResponseSummary from './ProspectPublicResponseSummary';
import ProspectFocusedPublicResponsePanel from './ProspectFocusedPublicResponsePanel';
import { parseFocusedPublicResponseIdentity } from './prospectFocusedPublicResponse.js';
import { ProspectPublicResponseActivity } from './ProspectOwnerResponseShared';
import {
  focusProspectPublicResponseSection,
  runProspectTimelineAppendWithAttention,
  runQuickFollowUpPostSaveRefresh,
  runQuickNotePostSaveTimelineRefresh,
  shouldRunProspectRefreshStep,
} from './adminProspectQuickFollowUpController';
import { ADMIN_PROSPECT_PUBLIC_RESPONSE_ANCHOR } from './adminProspectQuickFollowUp.js';
import {
  createAdminProspectTimelineLoad,
  isProspectTimelineBusyClaimHeld,
} from './adminProspectTimelineLoad';
import {
  buildProspectDetailSectionSummary,
  createProspectDetailSectionOpenState,
  ensureProspectDetailSectionOpen,
  requestProspectDetailSectionAttention,
  subscribeProspectDetailMobileViewport,
  toggleProspectDetailSection,
} from './adminProspectDetailMobileSections';
import {
  createAdminProspectVerificationTracker,
  freezeAdminProspectVerificationAction,
  shouldApplyAdminProspectVerificationResult,
  shouldStartAdminProspectOwnedLoad,
} from './adminProspectVerificationOwnership';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminProspectActivityTypeLabel,
  adminProspectListErrorKey,
  adminProspectSourceLabel,
  adminProspectStatusBadgeClass,
  adminProspectAdditionalOwners,
  adminProspectPrimaryOwner,
  adminProspectStatusLabel,
  adminProspectVerificationErrorKey,
  classifyAdminCallableError,
  formatAdminDate,
  formatAdminProspectAddress,
  formatAdminProspectUnits,
  isFollowUpOverdue,
  shouldApplyAdminProspectOwnedResult,
} from './adminProspectDisplay';

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-900">{children}</dd>
    </div>
  );
}

function Section({ title, children, stacked = false, hideTitle = false, embedded = false }) {
  // Always <section> so breakpoint chrome changes do not remount descendants.
  const chrome = embedded ? 'min-w-0' : 'rounded-xl border border-slate-200 bg-white p-4';
  const bodyClass = stacked
    ? hideTitle
      ? undefined
      : 'mt-3'
    : hideTitle
      ? 'grid gap-3 sm:grid-cols-2'
      : 'mt-3 grid gap-3 sm:grid-cols-2';
  return (
    <section className={chrome} data-embedded={embedded ? 'true' : 'false'}>
      {!hideTitle && title ? <h2 className="text-sm font-semibold text-slate-800">{title}</h2> : null}
      {stacked ? <div className={bodyClass}>{children}</div> : <dl className={bodyClass}>{children}</dl>}
    </section>
  );
}

function StatusBadge({ status, t }) {
  const label = adminProspectStatusLabel(status, t);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${adminProspectStatusBadgeClass(status)}`}
    >
      {label}
    </span>
  );
}

const SECTION_PROVENANCE = {
  primaryContact: (detail) => detail?.primaryContact?.provenance,
  latestOwnerProvidedPropertyProfile: (detail) => detail?.latestOwnerProvidedPropertyProfile?.provenance,
  latestOwnerProvidedFinancialSnapshot: (detail) => detail?.latestOwnerProvidedFinancialSnapshot?.provenance,
};

export default function AdminProspectDetailPage() {
  const { prospectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const { adminRole, currentUser } = useAuth();
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState('loading');
  const [errorKind, setErrorKind] = useState(null);
  const [activities, setActivities] = useState([]);
  const [timelineStatus, setTimelineStatus] = useState('loading');
  const [timelineErrorKind, setTimelineErrorKind] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [timelineBusy, setTimelineBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [propertyDataEditOpen, setPropertyDataEditOpen] = useState(false);
  const [analysisConversionOpen, setAnalysisConversionOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [conversionRefreshWarning, setConversionRefreshWarning] = useState(false);
  const [verificationBusy, setVerificationBusy] = useState(null);
  const [verificationPending, setVerificationPending] = useState(null);
  const [verificationError, setVerificationError] = useState(null);
  const [verificationRefreshWarning, setVerificationRefreshWarning] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(() => createProspectDetailSectionOpenState());
  const [focusedPublicResponse, setFocusedPublicResponse] = useState(null);
  const detailGateRef = useRef(createAdminProspectRequestGate());
  const verifyTrackerRef = useRef(createAdminProspectVerificationTracker());
  const timelineGateRef = useRef(createAdminProspectRequestGate());
  const timelineBusyClaimRef = useRef(0);
  const mountedRef = useRef(true);
  const prospectIdRef = useRef('');
  const routeVisitIdRef = useRef(0);
  const authUidRef = useRef('');
  const [routeVisitId, setRouteVisitId] = useState(0);

  const id = String(prospectId || '').trim();
  prospectIdRef.current = id;
  authUidRef.current = currentUser?.uid ? String(currentUser.uid) : '';
  const canConvertAnalysis = canManageRoles(adminRole);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => subscribeProspectDetailMobileViewport(setIsMobileLayout), []);

  const loadDetail = useCallback(
    async ({ silent = false, ownerProspectId: ownerOverride, visitId: visitOverride } = {}) => {
      const ownerProspectId = ownerOverride ?? id;
      const visitBound = ownerOverride != null || visitOverride != null;
      const requestVisitId = visitOverride ?? routeVisitIdRef.current;

      const refreshGuard = (requestId) =>
        shouldRunProspectRefreshStep({
          ownerProspectId,
          requestVisitId,
          getActiveProspectId: () => prospectIdRef.current,
          getActiveVisitId: () => routeVisitIdRef.current,
          getMounted: () => mountedRef.current,
          requestId,
          isCurrentRequest: (rid) => detailGateRef.current.isCurrent(rid),
        });

      if (!ownerProspectId) {
        if (!visitBound) {
          setDetail(null);
          setStatus('not-found');
        }
        return { ok: false, applied: true };
      }
      if (visitBound) {
        if (!refreshGuard(null).ok) return { ok: false, applied: false };
      } else if (!shouldStartAdminProspectOwnedLoad({ ownerProspectId, activeProspectId: prospectIdRef.current })) {
        return { ok: false, applied: false };
      }

      const requestId = detailGateRef.current.next();
      if (!silent && !visitBound) {
        setStatus('loading');
        setErrorKind(null);
      }
      try {
        const data = await getAdminProspect(ownerProspectId);
        if (visitBound) {
          if (!refreshGuard(requestId).ok) return { ok: false, applied: false };
        } else if (
          !shouldApplyAdminProspectOwnedResult({
            mounted: mountedRef.current,
            requestId,
            activeRequestId: detailGateRef.current.isCurrent(requestId) ? requestId : null,
            ownerProspectId,
            activeProspectId: prospectIdRef.current,
          })
        ) {
          return { ok: false, applied: false };
        }
        if (!visitBound) {
          setDetail(data);
          setStatus('ready');
          setVerificationError(null);
          setVerificationRefreshWarning(false);
        } else if (refreshGuard(requestId).ok) {
          setDetail(data);
          setVerificationError(null);
          setVerificationRefreshWarning(false);
        }
        return { ok: true, applied: true, data };
      } catch (err) {
        if (visitBound) {
          if (!refreshGuard(requestId).ok) return { ok: false, applied: false };
        } else if (
          !shouldApplyAdminProspectOwnedResult({
            mounted: mountedRef.current,
            requestId,
            activeRequestId: detailGateRef.current.isCurrent(requestId) ? requestId : null,
            ownerProspectId,
            activeProspectId: prospectIdRef.current,
          })
        ) {
          return { ok: false, applied: false };
        }
        const kind = classifyAdminCallableError(err);
        if (!silent && !visitBound) {
          setDetail(null);
          setErrorKind(kind);
          setStatus(kind === 'not-found' ? 'not-found' : 'error');
        }
        return { ok: false, applied: true, kind };
      }
    },
    [id],
  );

  const loadTimeline = useCallback(
    createAdminProspectTimelineLoad({
      getDefaultProspectId: () => id,
      getActiveProspectId: () => prospectIdRef.current,
      getActiveVisitId: () => routeVisitIdRef.current,
      getMounted: () => mountedRef.current,
      getRouteVisitId: () => routeVisitIdRef.current,
      timelineGate: timelineGateRef.current,
      busyClaimRef: timelineBusyClaimRef,
      setBusy: setTimelineBusy,
      setTimelineStatus,
      setTimelineErrorKind,
      setActivities,
      setNextPageToken,
      listActivities: listAdminProspectActivities,
      buildListRequest: buildListAdminProspectActivitiesRequest,
      pageSize: ADMIN_PROSPECTS_PAGE_SIZE,
      classifyError: classifyAdminCallableError,
    }),
    [id],
  );

  useEffect(() => {
    routeVisitIdRef.current += 1;
    setRouteVisitId(routeVisitIdRef.current);
    verifyTrackerRef.current.invalidate();
    setVerificationBusy(null);
    setVerificationPending(null);
    setVerificationError(null);
    setVerificationRefreshWarning(false);
    setConversionRefreshWarning(false);
    setAnalysisConversionOpen(false);
    setSectionOpen(createProspectDetailSectionOpenState());
    setFocusedPublicResponse(null);
    timelineBusyClaimRef.current = 0;
    setTimelineBusy(false);
    loadDetail();
    loadTimeline();
  }, [id]);

  // Workspace / notifications → Immo 360: expand public responses, optional exact focus (HashRouter-safe).
  useEffect(() => {
    const anchor = location.state?.focusPublicResponseAnchor;
    if (!anchor || status !== 'ready' || !detail) return;

    const focusIdentity = parseFocusedPublicResponseIdentity({
      campaignId: location.state?.focusPublicResponseCampaignId,
      mailingId: location.state?.focusPublicResponseMailingId,
      intent: location.state?.focusPublicResponseIntent,
    });
    if (focusIdentity) {
      setFocusedPublicResponse(focusIdentity);
    }

    const result = focusProspectPublicResponseSection({
      documentObj: typeof document !== 'undefined' ? document : null,
      locationObj: typeof window !== 'undefined' ? window.location : null,
      anchorId: typeof anchor === 'string' ? anchor : ADMIN_PROSPECT_PUBLIC_RESPONSE_ANCHOR,
      ensureExpanded: () => {
        let didExpand = false;
        setSectionOpen((prev) => {
          const next = ensureProspectDetailSectionOpen(prev, 'publicResponses');
          didExpand = next !== prev && next?.publicResponses === true;
          return next;
        });
        return { didExpand: true };
      },
    });
    if (result?.ok || result?.reason === 'missing' || result?.reason === 'unavailable') {
      // Clear location focus keys but keep focusedPublicResponse in component state for this visit.
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: {} },
      );
    }
  }, [status, detail, location.state, location.pathname, location.search, navigate]);

  const handleSaved = async () => {
    setSuccessMessage(t('admin.prospection.edit.success'));
    await loadDetail();
    await loadTimeline();
  };

  const handleAnalysisConverted = async ({
    mode,
    idempotent,
    prospect: returnedProspect,
    propertyId,
    visitId: requestVisitId,
    prospectId: requestProspectId,
    authUid: requestAuthUid,
  } = {}) => {
    const ownerProspectId = String(requestProspectId || id || '').trim();
    const ownership = {
      requestProspectId: ownerProspectId,
      requestVisitId: requestVisitId ?? routeVisitIdRef.current,
      activeProspectId: prospectIdRef.current,
      activeVisitId: routeVisitIdRef.current,
      requestAuthUid: requestAuthUid ?? authUidRef.current,
      activeAuthUid: authUidRef.current,
    };
    if (!shouldApplyConversionResult(ownership)) {
      return { applied: false, writeCommitted: false };
    }

    const writeState = markConversionWriteSuccess(null, {
      propertyId,
      prospect: returnedProspect,
      mode,
      idempotent,
    });
    const refreshPlan = planPostConversionRefresh({ writeCommitted: writeState.writeCommitted });

    const successKey = idempotent
      ? 'admin.prospection.analysisConversion.success.idempotent'
      : mode === 'link'
        ? 'admin.prospection.analysisConversion.success.link'
        : 'admin.prospection.analysisConversion.success.create';

    // Optimistic detail update without remounting quick follow-up (preserves dirty drafts).
    if (returnedProspect && typeof returnedProspect === 'object' && returnedProspect.id === ownerProspectId) {
      setDetail((current) => {
        if (!shouldApplyConversionResult({
          ...ownership,
          activeProspectId: prospectIdRef.current,
          activeVisitId: routeVisitIdRef.current,
          activeAuthUid: authUidRef.current,
        })) {
          return current;
        }
        return { ...(current || {}), ...returnedProspect };
      });
    }

    setSuccessMessage(t(successKey));
    setConversionRefreshWarning(false);
    setAnalysisConversionOpen(false);

    if (!refreshPlan.shouldRefresh) {
      return { applied: true, writeCommitted: true, refreshOk: null };
    }

    const refresh = await runQuickFollowUpPostSaveRefresh({
      ownerProspectId,
      requestVisitId: ownership.requestVisitId,
      getActiveProspectId: () => prospectIdRef.current,
      getActiveVisitId: () => routeVisitIdRef.current,
      getMounted: () => mountedRef.current,
      loadDetail: (opts) => loadDetail({ silent: true, ...opts }),
      loadTimeline: (opts) => loadTimeline({ ...opts }),
    });

    if (!shouldApplyConversionResult({
      ...ownership,
      activeProspectId: prospectIdRef.current,
      activeVisitId: routeVisitIdRef.current,
      activeAuthUid: authUidRef.current,
    })) {
      return { applied: false, writeCommitted: true, refreshOk: null };
    }

    if (!refresh.refreshOk) {
      markConversionRefreshFailed(writeState);
      // Top-level banner — visible even when the follow-up/timeline section is collapsed.
      setConversionRefreshWarning(true);
      return {
        applied: true,
        writeCommitted: true,
        refreshOk: false,
        outcome: 'writeCommittedRefreshFailed',
      };
    }

    setConversionRefreshWarning(false);
    return {
      applied: true,
      writeCommitted: true,
      refreshOk: true,
      outcome: 'writeCommittedRefreshOk',
    };
  };

  const handleQuickFollowUpSaved = async ({
    prospectId: savedId,
    visitId: requestVisitId,
    conflictReload = false,
    refreshOnly = false,
  } = {}) => {
    const ownerProspectId = savedId || id;
    if (savedId && savedId !== prospectIdRef.current) {
      return { refreshOk: false, applied: false, prospect: null };
    }
    if (requestVisitId != null && requestVisitId !== routeVisitIdRef.current) {
      return { refreshOk: false, applied: false, prospect: null };
    }

    const result = await runQuickFollowUpPostSaveRefresh({
      ownerProspectId,
      requestVisitId,
      getActiveProspectId: () => prospectIdRef.current,
      getActiveVisitId: () => routeVisitIdRef.current,
      getMounted: () => mountedRef.current,
      loadDetail: (opts) => loadDetail({ silent: true, ...opts }),
      loadTimeline: (opts) => loadTimeline({ ...opts }),
      conflictReload,
      refreshOnly,
    });

    if (
      result.refreshOk &&
      !conflictReload &&
      !refreshOnly &&
      shouldRunProspectRefreshStep({
        ownerProspectId,
        requestVisitId,
        getActiveProspectId: () => prospectIdRef.current,
        getActiveVisitId: () => routeVisitIdRef.current,
        getMounted: () => mountedRef.current,
      }).ok
    ) {
      setSuccessMessage(t('admin.prospection.quickFollowUp.success'));
    }

    return result;
  };

  const handleDelete = async () => {
    setDeleteSaving(true);
    setDeleteError('');
    try {
      await deleteAdminProspect(id);
      navigate('/admin/prospection', { state: { prospectDeleted: true } });
    } catch (err) {
      setDeleteError(t(adminProspectListErrorKey(classifyAdminCallableError(err))));
      setDeleteSaving(false);
    }
  };

  const ownsVerificationResult = (owner) =>
    shouldApplyAdminProspectVerificationResult({
      mounted: mountedRef.current,
      owner,
      tracker: verifyTrackerRef.current,
      activeProspectId: prospectIdRef.current,
    });

  const handleVerificationRequest = ({ section }) => {
    const action = freezeAdminProspectVerificationAction({
      prospectId: id,
      section,
      currentVerificationState: detail?.[section]?.verificationState,
      provenance: SECTION_PROVENANCE[section]?.(detail),
    });
    if (!action) {
      setVerificationError({
        section,
        message: t('admin.prospection.ownerResponse.verification.stale'),
        stale: true,
      });
      const attentionSection =
        section === 'primaryContact'
          ? 'primaryContact'
          : section === 'latestOwnerProvidedPropertyProfile'
            ? 'ownerProvidedProperty'
            : section === 'latestOwnerProvidedFinancialSnapshot'
              ? 'ownerProvidedFinancial'
              : null;
      if (attentionSection) {
        requestSectionAttention({
          sectionId: attentionSection,
          ownerProspectId: id,
          visitId: routeVisitIdRef.current,
          reason: 'verification-stale',
        });
      }
      return;
    }
    setVerificationError(null);
    setVerificationPending(action);
  };

  const handleVerificationConfirm = async () => {
    const pending = verificationPending;
    if (!pending || verificationBusy) return;
    if (pending.prospectId !== prospectIdRef.current) return;
    const owner = verifyTrackerRef.current.begin(pending.prospectId);
    setVerificationBusy(pending.section);
    setVerificationError(null);
    try {
      await setAdminProspectProjectionVerification(
        buildSetAdminProspectProjectionVerificationRequest({
          prospectId: pending.prospectId,
          section: pending.section,
          verificationState: pending.verificationState,
          expectedVerificationState: pending.expectedVerificationState,
          expectedSource: pending.expectedSource,
        }),
      );
      if (!ownsVerificationResult(owner)) return;
      setVerificationPending(null);
      const refresh = await loadDetail({ silent: true });
      if (!ownsVerificationResult(owner)) return;
      if (!refresh?.ok) {
        setVerificationRefreshWarning(true);
      }
    } catch (err) {
      if (!ownsVerificationResult(owner)) return;
      const kind = classifyAdminCallableError(err);
      setVerificationError({
        section: pending.section,
        message: t(adminProspectVerificationErrorKey(kind)),
        stale: kind === 'failed-precondition',
      });
      const attentionSection =
        pending.section === 'primaryContact'
          ? 'primaryContact'
          : pending.section === 'latestOwnerProvidedPropertyProfile'
            ? 'ownerProvidedProperty'
            : pending.section === 'latestOwnerProvidedFinancialSnapshot'
              ? 'ownerProvidedFinancial'
              : null;
      if (attentionSection) {
        requestSectionAttention({
          sectionId: attentionSection,
          ownerProspectId: pending.prospectId,
          visitId: routeVisitIdRef.current,
          reason: 'verification-error',
        });
      }
    } finally {
      if (ownsVerificationResult(owner)) {
        setVerificationBusy(null);
      }
    }
  };

  const addressLabel = formatAdminProspectAddress(detail) || t('admin.prospection.detail.title');
  const overdue = isFollowUpOverdue(detail?.nextFollowUpAt);
  const primaryOwner = adminProspectPrimaryOwner(detail);
  const additionalOwners = adminProspectAdditionalOwners(detail);
  const convertedPropertyId =
    typeof detail?.convertedPropertyId === 'string' && detail.convertedPropertyId.trim()
      ? detail.convertedPropertyId.trim()
      : null;
  const isLegacyConvertedWithoutLink = detail?.status === 'converted' && !convertedPropertyId;
  const canOpenAnalysisConversion =
    Boolean(detail) && !convertedPropertyId && canConvertAnalysis;
  const openAnalysisSearch = convertedPropertyId
    ? searchWithAppStepAndProperty('', 'form', convertedPropertyId)
    : '';
  const errorMessage = t(adminProspectListErrorKey(errorKind));
  const timelineErrorMessage = t(adminProspectListErrorKey(timelineErrorKind));
  const verificationProps = {
    busy: Boolean(verificationBusy),
    pending: verificationPending,
    onRequestChange: handleVerificationRequest,
    onConfirm: handleVerificationConfirm,
    onCancel: () => setVerificationPending(null),
    onReload: () => {
      loadDetail({ silent: true });
    },
  };

  const toggleSection = (sectionId) => {
    setSectionOpen((current) => toggleProspectDetailSection(current, sectionId));
  };

  const ensureSectionExpanded = (sectionId) => {
    const alreadyOpen = Boolean(sectionOpen?.[sectionId]);
    if (!alreadyOpen) {
      setSectionOpen((current) => ensureProspectDetailSectionOpen(current, sectionId));
    }
    return { didExpand: isMobileLayout && !alreadyOpen };
  };

  const requestSectionAttention = useCallback(
    ({
      sectionId,
      ownerProspectId: attentionOwner = id,
      visitId: attentionVisit = routeVisitIdRef.current,
      reason = 'attention',
    } = {}) => {
      let result = { ok: false, didExpand: false };
      setSectionOpen((current) => {
        result = requestProspectDetailSectionAttention(current, {
          sectionId,
          ownerProspectId: attentionOwner,
          requestVisitId: attentionVisit,
          getActiveProspectId: () => prospectIdRef.current,
          getActiveVisitId: () => routeVisitIdRef.current,
          getMounted: () => mountedRef.current,
          reason,
        });
        return result.ok ? result.openState : current;
      });
      return result;
    },
    [id],
  );

  const handleQuickNoteSaved = async ({ prospectId: savedId, visitId: requestVisitId } = {}) => {
    const ownerProspectId = savedId || id;
    if (savedId && savedId !== prospectIdRef.current) {
      return { ok: false, applied: false, noteSaved: true, timelineOk: null, didReveal: false, reason: 'stale' };
    }
    if (requestVisitId != null && requestVisitId !== routeVisitIdRef.current) {
      return { ok: false, applied: false, noteSaved: true, timelineOk: null, didReveal: false, reason: 'stale' };
    }

    return runQuickNotePostSaveTimelineRefresh({
      ownerProspectId,
      requestVisitId,
      getActiveProspectId: () => prospectIdRef.current,
      getActiveVisitId: () => routeVisitIdRef.current,
      getMounted: () => mountedRef.current,
      loadTimeline: (opts) => loadTimeline({ ...opts }),
      requestSectionAttention,
    });
  };

  const handleTimelineLoadMore = async () => {
    if (!nextPageToken || timelineBusy || isProspectTimelineBusyClaimHeld(timelineBusyClaimRef)) return;
    return runProspectTimelineAppendWithAttention({
      ownerProspectId: id,
      requestVisitId: routeVisitIdRef.current,
      getActiveProspectId: () => prospectIdRef.current,
      getActiveVisitId: () => routeVisitIdRef.current,
      getMounted: () => mountedRef.current,
      loadTimeline: (opts) => loadTimeline({ ...opts }),
      pageToken: nextPageToken,
      requestSectionAttention,
    });
  };

  const sectionSummary = (sectionId) =>
    buildProspectDetailSectionSummary(sectionId, {
      detail,
      activities,
      timelineStatus,
      t,
    });

  const embed = isMobileLayout;

  const activityHistory = (
    <section
      className={embed ? 'min-w-0' : 'rounded-xl border border-slate-200 bg-white p-4'}
      data-testid="admin-prospect-activity-history"
      data-embedded={embed ? 'true' : 'false'}
    >
      <h3 className="text-sm font-semibold text-slate-800">
        {t('admin.prospection.section.activityHistory')}
      </h3>

      {timelineStatus === 'loading' ? (
        <p className="mt-4 text-sm text-slate-500" aria-live="polite">
          {t('admin.prospection.timeline.loading')}
        </p>
      ) : null}
      {timelineStatus === 'empty' ? (
        <p className="mt-4 text-sm text-slate-600">{t('admin.prospection.timeline.empty')}</p>
      ) : null}
      {timelineStatus === 'error' ? (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <p>{timelineErrorMessage}</p>
          <button
            type="button"
            onClick={() => loadTimeline()}
            className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {t('admin.prospection.retry')}
          </button>
        </div>
      ) : null}
      {activities.length > 0 && (timelineStatus === 'ready' || timelineStatus === 'error') ? (
        <ol className="mt-4 space-y-3">
          {activities.map((item) => (
            <li key={item.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {formatAdminDate(item.createdAt, language)}
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {adminProspectActivityTypeLabel(item.type, t)}
              </p>
              {item.type === 'note' ? (
                <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800">{item.body}</p>
              ) : null}
              {item.type === 'status_change' ? (
                <p className="mt-1 text-sm text-slate-800">
                  {adminProspectStatusLabel(item.fromStatus, t)}
                  {' → '}
                  {adminProspectStatusLabel(item.toStatus, t)}
                </p>
              ) : null}
              {item.type === 'public_response' ? (
                <ProspectPublicResponseActivity item={item} language={language} t={t} />
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
      {nextPageToken ? (
        <button
          type="button"
          disabled={timelineBusy}
          onClick={() => handleTimelineLoadMore()}
          className="mt-3 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
        >
          {t('admin.prospection.timeline.more')}
        </button>
      ) : null}
    </section>
  );

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-2">
          <MapPinned className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {t('admin.prospection.detail.title')}
            </p>
            <h1 className="mt-0.5 text-xl font-semibold text-slate-900">{addressLabel}</h1>
            {status === 'ready' && detail ? (
              <div className="mt-2">
                <StatusBadge status={detail.status} t={t} />
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/admin/prospection"
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('admin.prospection.detail.back')}
          </Link>
          {status === 'ready' ? (
            <>
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {t('admin.prospection.edit')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setDeleteError('');
                  setDeleteOpen(true);
                }}
                className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {t('admin.prospection.delete')}
              </button>
            </>
          ) : null}
        </div>
      </div>

      {successMessage ? (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" role="status">
          {successMessage}
        </p>
      ) : null}

      {conversionRefreshWarning ? (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950"
          role="alert"
          data-testid="analysis-conversion-refresh-warning"
        >
          <p>{t('admin.prospection.analysisConversion.refreshFailed')}</p>
          <button
            type="button"
            onClick={async () => {
              const refresh = await runQuickFollowUpPostSaveRefresh({
                ownerProspectId: id,
                requestVisitId: routeVisitIdRef.current,
                getActiveProspectId: () => prospectIdRef.current,
                getActiveVisitId: () => routeVisitIdRef.current,
                getMounted: () => mountedRef.current,
                loadDetail: (opts) => loadDetail({ silent: true, ...opts }),
                loadTimeline: (opts) => loadTimeline({ ...opts }),
                refreshOnly: true,
              });
              if (refresh.refreshOk) setConversionRefreshWarning(false);
            }}
            className="mt-2 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-950 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            {t('admin.prospection.analysisConversion.refreshRetry')}
          </button>
        </div>
      ) : null}

      {verificationRefreshWarning ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" role="alert">
          <p>{t('admin.prospection.ownerResponse.verification.refreshFailed')}</p>
          <button
            type="button"
            onClick={() => loadDetail({ silent: true })}
            className="mt-2 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-950 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            {t('admin.prospection.ownerResponse.verification.reload')}
          </button>
        </div>
      ) : null}

      {status === 'loading' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.prospection.detail.loading')}
        </p>
      ) : null}
      {status === 'not-found' ? (
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          {t('admin.prospection.detail.notFound')}
        </p>
      ) : null}
      {status === 'error' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{errorMessage}</p>
          <button
            type="button"
            onClick={loadDetail}
            className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {t('admin.prospection.retry')}
          </button>
        </div>
      ) : null}

      {status === 'ready' && detail ? (
        <>
          <AdminProspectDetailMobileSection
            sectionId="followUp"
            title={t('admin.prospection.section.followUpActivity')}
            summary={sectionSummary('followUp')}
            isMobile={isMobileLayout}
            open={sectionOpen.followUp}
            onToggle={() => toggleSection('followUp')}
          >
            <div className="space-y-4">
              <h2
                className={
                  isMobileLayout
                    ? 'sr-only'
                    : 'text-sm font-semibold text-slate-800'
                }
              >
                {t('admin.prospection.section.followUpActivity')}
              </h2>
              <AdminProspectQuickFollowUpPanel
                prospect={detail}
                prospectId={id}
                visitId={routeVisitId}
                activeProspectId={id}
                language={language}
                t={t}
                hideTitle
                onFollowUpSaved={handleQuickFollowUpSaved}
                onNoteSaved={handleQuickNoteSaved}
                onEnsurePublicResponsesExpanded={() => ensureSectionExpanded('publicResponses')}
                onSectionAttention={requestSectionAttention}
              />
              {convertedPropertyId ? (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {t('admin.prospection.analysisConversion.linked.label')}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Link
                      to={{ pathname: '/', search: openAnalysisSearch }}
                      className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {t('admin.prospection.analysisConversion.openAnalysis')}
                    </Link>
                    <Link
                      to={`/admin/properties/${encodeURIComponent(convertedPropertyId)}`}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {t('admin.prospection.analysisConversion.linked.adminReadonly')}
                    </Link>
                  </div>
                </div>
              ) : null}
              {canOpenAnalysisConversion ? (
                <div>
                  <button
                    type="button"
                    className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={() => setAnalysisConversionOpen(true)}
                  >
                    {t('admin.prospection.analysisConversion.open')}
                  </button>
                </div>
              ) : null}
              {!canConvertAnalysis && !convertedPropertyId && detail ? (
                <p className="text-xs text-slate-500" role="status">
                  {t('admin.prospection.analysisConversion.superAdminOnly')}
                </p>
              ) : null}
              {activityHistory}
            </div>
          </AdminProspectDetailMobileSection>

          <div className={isMobileLayout ? 'space-y-4' : 'grid gap-4 lg:grid-cols-2'}>
            <AdminProspectDetailMobileSection
              sectionId="building"
              title={t('admin.prospection.section.building')}
              summary={sectionSummary('building')}
              isMobile={isMobileLayout}
              open={sectionOpen.building}
              onToggle={() => toggleSection('building')}
            >
              <Section
                title={t('admin.prospection.section.building')}
                hideTitle={embed}
                embedded={embed}
              >
                <Field label={t('admin.prospection.field.address')}>
                  {detail.address || ADMIN_DATE_PLACEHOLDER}
                </Field>
                <Field label={t('admin.prospection.field.city')}>{detail.city || ADMIN_DATE_PLACEHOLDER}</Field>
                <Field label={t('admin.prospection.field.province')}>
                  {detail.province ? t(`admin.prospection.province.${detail.province}`) : ADMIN_DATE_PLACEHOLDER}
                </Field>
                <Field label={t('admin.prospection.field.postalCode')}>
                  {detail.postalCode || ADMIN_DATE_PLACEHOLDER}
                </Field>
                <Field label={t('admin.prospection.field.units')}>
                  {formatAdminProspectUnits(detail.numberOfUnits)}
                </Field>
              </Section>
            </AdminProspectDetailMobileSection>

            <AdminProspectDetailMobileSection
              sectionId="pipeline"
              title={t('admin.prospection.section.pipeline')}
              summary={sectionSummary('pipeline')}
              isMobile={isMobileLayout}
              open={sectionOpen.pipeline}
              onToggle={() => toggleSection('pipeline')}
            >
              <Section
                title={t('admin.prospection.section.pipeline')}
                hideTitle={embed}
                embedded={embed}
              >
                <Field label={t('admin.prospection.field.status')}>
                  <StatusBadge status={detail.status} t={t} />
                </Field>
                <Field label={t('admin.prospection.field.source')}>
                  {adminProspectSourceLabel(detail.source, t)}
                </Field>
                <Field label={t('admin.prospection.field.lastContactAt')}>
                  {formatAdminDate(detail.lastContactAt, language)}
                </Field>
                <Field label={t('admin.prospection.field.nextFollowUpAt')}>
                  <span className={overdue ? 'font-semibold text-amber-900' : undefined}>
                    {formatAdminDate(detail.nextFollowUpAt, language)}
                  </span>
                  {overdue ? (
                    <span className="ml-2 text-xs font-semibold text-amber-800">
                      {t('admin.prospection.followUp.overdue')}
                    </span>
                  ) : null}
                </Field>
                <Field label={t('admin.prospection.field.createdAt')}>
                  {formatAdminDate(detail.createdAt, language)}
                </Field>
                <Field label={t('admin.prospection.field.updatedAt')}>
                  {formatAdminDate(detail.updatedAt, language)}
                </Field>
                {convertedPropertyId ? (
                  <div className="sm:col-span-2">
                    <Field label={t('admin.prospection.analysisConversion.linked.label')}>
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={{ pathname: '/', search: openAnalysisSearch }}
                          className="font-semibold text-indigo-700 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          {t('admin.prospection.analysisConversion.openAnalysis')}
                        </Link>
                        <Link
                          to={`/admin/properties/${encodeURIComponent(convertedPropertyId)}`}
                          className="text-sm text-slate-600 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          {convertedPropertyId}
                        </Link>
                      </div>
                      {detail.convertedAt ? (
                        <span className="mt-1 block text-xs text-slate-500">
                          {t('admin.prospection.analysisConversion.linked.convertedAt')}{' '}
                          {formatAdminDate(detail.convertedAt, language)}
                        </span>
                      ) : null}
                    </Field>
                  </div>
                ) : null}
                {isLegacyConvertedWithoutLink && canConvertAnalysis ? (
                  <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm text-amber-950">
                      {t('admin.prospection.analysisConversion.legacy.repair')}
                    </p>
                    <button
                      type="button"
                      className="mt-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={() => setAnalysisConversionOpen(true)}
                    >
                      {t('admin.prospection.analysisConversion.open')}
                    </button>
                  </div>
                ) : null}
                {isLegacyConvertedWithoutLink && !canConvertAnalysis ? (
                  <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm text-amber-950">
                      {t('admin.prospection.analysisConversion.legacy.repair')}
                    </p>
                    <p className="mt-2 text-xs text-amber-900">
                      {t('admin.prospection.analysisConversion.superAdminOnly')}
                    </p>
                  </div>
                ) : null}
              </Section>
            </AdminProspectDetailMobileSection>
          </div>

          <div className={isMobileLayout ? 'space-y-4' : 'grid gap-4 lg:grid-cols-2'}>
            <AdminProspectDetailMobileSection
              sectionId="primaryContact"
              title={t('admin.prospection.section.primaryContact')}
              summary={sectionSummary('primaryContact')}
              isMobile={isMobileLayout}
              open={sectionOpen.primaryContact}
              onToggle={() => toggleSection('primaryContact')}
            >
              <ProspectPrimaryContactCard
                contact={detail.primaryContact}
                language={language}
                t={t}
                hideTitle={embed}
                embedded={embed}
                error={verificationError?.section === 'primaryContact' ? verificationError : null}
                {...verificationProps}
              />
            </AdminProspectDetailMobileSection>

            <AdminProspectDetailMobileSection
              sectionId="owners"
              title={t('admin.prospection.section.owner')}
              summary={sectionSummary('owners')}
              isMobile={isMobileLayout}
              open={sectionOpen.owners}
              onToggle={() => toggleSection('owners')}
            >
              <Section
                title={t('admin.prospection.section.owner')}
                hideTitle={embed}
                embedded={embed}
              >
                <div className="sm:col-span-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                    {t('admin.prospection.field.primaryOwner')}
                  </p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    <Field label={t('admin.prospection.field.ownerName')}>
                      {primaryOwner.name || ADMIN_DATE_PLACEHOLDER}
                    </Field>
                    <Field label={t('admin.prospection.field.ownerMailingAddress')}>
                      {primaryOwner.mailingAddress || ADMIN_DATE_PLACEHOLDER}
                    </Field>
                  </div>
                </div>
                {additionalOwners.length ? (
                  <div className="sm:col-span-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      {t('admin.prospection.field.additionalOwners')}
                    </p>
                    <div className="mt-2 grid gap-3">
                      {additionalOwners.map((owner, index) => (
                        <div key={`additional-owner-${index}`} className="grid gap-3 sm:grid-cols-2">
                          <Field label={t('admin.prospection.field.ownerName')}>
                            {owner.name || ADMIN_DATE_PLACEHOLDER}
                          </Field>
                          <Field label={t('admin.prospection.field.ownerMailingAddress')}>
                            {owner.mailingAddress || ADMIN_DATE_PLACEHOLDER}
                          </Field>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </Section>
            </AdminProspectDetailMobileSection>
          </div>

          <AdminProspectDetailMobileSection
            sectionId="importedProperty"
            title={t('admin.prospection.importedProperty.title')}
            summary={sectionSummary('importedProperty')}
            isMobile={isMobileLayout}
            open={sectionOpen.importedProperty}
            onToggle={() => toggleSection('importedProperty')}
          >
            <ProspectImportedPropertyCard
              detail={detail}
              language={language}
              t={t}
              hideTitle={embed}
              embedded={embed}
              onEdit={() => setPropertyDataEditOpen(true)}
            />
          </AdminProspectDetailMobileSection>

          <AdminProspectDetailMobileSection
            sectionId="importHistory"
            title={t('admin.prospection.importHistory.title')}
            summary={sectionSummary('importHistory')}
            isMobile={isMobileLayout}
            open={sectionOpen.importHistory}
            onToggle={() => toggleSection('importHistory')}
          >
            <ProspectImportHistorySection
              prospectId={id}
              language={language}
              t={t}
              mobileLayout={isMobileLayout}
              mobileActive={Boolean(sectionOpen.importHistory)}
              hideTitle={embed}
              embedded={embed}
            />
          </AdminProspectDetailMobileSection>

          <AdminProspectDetailMobileSection
            sectionId="associatedCampaigns"
            title={t('admin.prospection.associatedCampaigns.title')}
            summary={sectionSummary('associatedCampaigns')}
            isMobile={isMobileLayout}
            open={sectionOpen.associatedCampaigns}
            onToggle={() => toggleSection('associatedCampaigns')}
          >
            <ProspectAssociatedCampaignsSection
              prospectId={id}
              language={language}
              t={t}
              mobileLayout={isMobileLayout}
              mobileActive={Boolean(sectionOpen.associatedCampaigns)}
              hideTitle={embed}
              embedded={embed}
            />
          </AdminProspectDetailMobileSection>

          <AdminProspectDetailMobileSection
            sectionId="publicResponses"
            title={t('admin.prospection.section.responseSummary')}
            summary={sectionSummary('publicResponses')}
            isMobile={isMobileLayout}
            open={sectionOpen.publicResponses}
            onToggle={() => toggleSection('publicResponses')}
          >
            {focusedPublicResponse ? (
              <ProspectFocusedPublicResponsePanel
                campaignId={focusedPublicResponse.campaignId}
                mailingId={focusedPublicResponse.mailingId}
                intent={focusedPublicResponse.intent}
                visitId={routeVisitId}
                authUid={currentUser?.uid || null}
                language={language}
                t={t}
              />
            ) : null}
            <ProspectPublicResponseSummary
              summary={detail.publicResponseSummary}
              language={language}
              t={t}
              hideTitle={embed}
              embedded={embed}
            />
          </AdminProspectDetailMobileSection>

          <div className={isMobileLayout ? 'space-y-4' : 'grid gap-4 lg:grid-cols-2'}>
            <AdminProspectDetailMobileSection
              sectionId="ownerProvidedProperty"
              title={t('admin.prospection.section.ownerProvidedBuilding')}
              summary={sectionSummary('ownerProvidedProperty')}
              isMobile={isMobileLayout}
              open={sectionOpen.ownerProvidedProperty}
              onToggle={() => toggleSection('ownerProvidedProperty')}
            >
              <ProspectOwnerProvidedPropertyCard
                profile={detail.latestOwnerProvidedPropertyProfile}
                language={language}
                t={t}
                hideTitle={embed}
                embedded={embed}
                error={
                  verificationError?.section === 'latestOwnerProvidedPropertyProfile' ? verificationError : null
                }
                {...verificationProps}
              />
            </AdminProspectDetailMobileSection>

            <AdminProspectDetailMobileSection
              sectionId="ownerProvidedFinancial"
              title={t('admin.prospection.section.ownerProvidedFinancial')}
              summary={sectionSummary('ownerProvidedFinancial')}
              isMobile={isMobileLayout}
              open={sectionOpen.ownerProvidedFinancial}
              onToggle={() => toggleSection('ownerProvidedFinancial')}
            >
              <ProspectOwnerProvidedFinancialCard
                snapshot={detail.latestOwnerProvidedFinancialSnapshot}
                language={language}
                t={t}
                hideTitle={embed}
                embedded={embed}
                error={
                  verificationError?.section === 'latestOwnerProvidedFinancialSnapshot' ? verificationError : null
                }
                {...verificationProps}
              />
            </AdminProspectDetailMobileSection>
          </div>

          <AdminProspectDetailMobileSection
            sectionId="prospectContext"
            title={t('admin.prospection.section.notes')}
            summary={sectionSummary('prospectContext')}
            isMobile={isMobileLayout}
            open={sectionOpen.prospectContext}
            onToggle={() => toggleSection('prospectContext')}
          >
            <Section
              title={t('admin.prospection.section.notes')}
              stacked
              hideTitle={embed}
              embedded={embed}
            >
              <p className="text-xs text-slate-500">{t('admin.prospection.notes.hint')}</p>
              <p className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-800">
                {detail.notes || t('admin.prospection.notes.empty')}
              </p>
            </Section>
          </AdminProspectDetailMobileSection>
        </>
      ) : null}

      <AdminEditProspectDialog
        open={editOpen}
        prospect={detail}
        t={t}
        onClose={() => setEditOpen(false)}
        onSaved={handleSaved}
      />
      <AdminEditProspectPropertyDataDialog
        open={propertyDataEditOpen}
        prospect={detail}
        t={t}
        onClose={() => setPropertyDataEditOpen(false)}
        onSaved={handleSaved}
      />
      <AdminProspectAnalysisConversionDialog
        open={analysisConversionOpen && canConvertAnalysis}
        prospect={detail}
        visitId={routeVisitId}
        authUid={currentUser?.uid || null}
        language={language}
        t={t}
        onClose={() => setAnalysisConversionOpen(false)}
        onConverted={handleAnalysisConverted}
      />
      <AdminDeleteProspectDialog
        open={deleteOpen}
        address={formatAdminProspectAddress(detail)}
        t={t}
        saving={deleteSaving}
        error={deleteError}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
